import { FC } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import {
  Icon,
  Spacer,
  Stack,
  Tab,
  TabGroup,
  TextCaption,
  TextTitle,
  Tooltip,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';

import { WithLoadingState } from '../../router';
import { StudioPageProps } from '../../hooks';

const TABS = ['actions', 'media', 'assets'] as const;
type FilesTab = (typeof TABS)[number];

/**
 * Tab shell for the Files page. Tabs are routes rather than local state, so each one is
 * linkable and survives a reload — `TabGroup` is used purely as the bar (no `TabPanel`)
 * and the body comes from `<Outlet/>`. `PageBody` is not rendered here: `WithLoadingState`
 * already provides it.
 */
const FilesLayoutPage: FC<StudioPageProps> = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { projectId, botId } = useParams();

  const active: FilesTab =
    TABS.find((tab) => pathname.endsWith(`/${tab}`)) ?? 'actions';

  const labels: Record<FilesTab, string> = {
    actions:
      t('pro-studio/actions:tabs.actions', { defaultValue: 'Actions' }) ?? '',
    media: t('pro-studio/actions:tabs.media', { defaultValue: 'Media' }) ?? '',
    assets:
      t('pro-studio/actions:tabs.assets', { defaultValue: 'Assets' }) ?? '',
  };

  return (
    <>
      <Stack gutter="xsmall" alignItems="center">
        <TextTitle test-id="ps-files-page-title" size="small">
          <TranslationComponent i18nKey="pro-studio/navigation:nav_links.files">
            Files
          </TranslationComponent>
        </TextTitle>
        <Tooltip test-id="ps-files-page-info-tooltip" placement="right">
          <Icon
            test-id="ps-files-page-info-icon"
            name="info"
            color="gray"
            size="small"
          />
          <TextCaption variant="primary" slot="content">
            <TranslationComponent i18nKey="pro-studio/actions:subtitle.part1">
              Actions allow the designer to perform custom integrations and
              special logic.
            </TranslationComponent>
          </TextCaption>
        </Tooltip>
      </Stack>
      <Spacer size="small" />
      {/*
        `default`, not `flush`: the design is text tabs with an underline under the active
        one and a hairline rule across the content width. `flush` is the segmented
        pill-in-a-tray control, which stretches each tab to an equal share of the full
        width — a different component for a different job.
      */}
      <TabGroup variant="default" test-id="ps-files-tabs">
        {TABS.map((tab) => (
          <Tab
            key={tab}
            slot="tab"
            panel={tab}
            active={active === tab}
            test-id={`ps-files-tab-${tab}`}
            onClick={() => navigate(`/${projectId}/${botId}/files/${tab}`)}
          >
            {labels[tab]}
          </Tab>
        ))}
      </TabGroup>
      <Spacer size="medium" />
      <Outlet />
    </>
  );
};

export const FilesLayout = WithLoadingState(FilesLayoutPage);
